/** @jsxImportSource @emotion/react */
import { Labels, Widgets } from '@zip/business-components';

import { Paper as MuiPaper } from '@material-ui/core';
import * as Icons from '@zip/icons';
import { Tooltip } from 'components';
import { useMerchantData } from 'contexts';
import { mapMfaStatus, MfaEnrollmentStatus } from 'enums';
import { css, theme } from 'global';
import ProductStatusLabels from 'modules/products-and-offers-table/ProductStatusLabels';
import { ProductName } from 'modules/products-and-offers-table/TableTypesAndConstants';
import { FC } from 'react';
import { getStatusVariant } from 'utils/colouring';
import {
  currencyFormatter,
  dateFormatter,
  formatNumberOfDays,
  getDescriptionForInterestFreeMonths,
  productFormatter,
  statusFormatter,
} from 'utils/formatters';
import * as styles from './SummaryCard.styles';
import {
  CustomerCardProps,
  OrderCardProps,
  ProductConfigProps,
  UserCardProps,
} from './SummaryCardProps';

export const Order: FC<OrderCardProps> = ({
  order,
  selected = false,
  onClick,
  key,
  ...props
}) => {
  return (
    <MuiPaper
      css={styles.paper(selected, false)}
      onClick={onClick}
      key={key}
      {...props}
    >
      <div css={styles.paperContent}>
        <span style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h4 css={styles.date}>{dateFormatter(order?.orderDate, 'day')}</h4>

          {order && (
            <p css={[css.noBtmMrgn, css.noTopMrgn]}>
              {currencyFormatter(order.orderTotal)}
            </p>
          )}
        </span>
        <div css={styles.summary}>
          <div css={styles.customerDetails(false)}>
            <p className="fs-mask">
              {order.firstName
                ? `${order.firstName} ${order.lastName}`
                : order.lastName}
            </p>
            {order && <p>{order.orderReference ?? order.orderId}</p>}
          </div>
          <div css={styles.orderDetails}>
            <Labels.Basic
              variant={getStatusVariant(order?.status)}
              label={statusFormatter(order?.status)}
              css={styles.label}
            />
          </div>
        </div>
      </div>

      {order?.expireInDays >= 0 && order?.expireInDays < 30 && (
        <div css={styles.orderExpiry}>
          <p className="body3">
            Order expires {order?.expireInDays > 0 ? 'in' : ''}{' '}
            {formatNumberOfDays(order?.expireInDays).toLowerCase()}
          </p>
        </div>
      )}
    </MuiPaper>
  );
};

export const Customer: FC<CustomerCardProps> = ({
  customer,
  selected = false,
  onClick,
  key,
  ...props
}) => {
  // @ts-expect-error - nullables to handle CustomerDetail or CliRequest models
  const date = customer?.statusDate ?? customer?.updatedDateTime;
  return (
    <MuiPaper
      css={styles.paper(selected)}
      onClick={onClick}
      key={key}
      {...props}
    >
      <div css={styles.summary}>
        <div css={styles.customerDetails(true)}>
          <h4 css={styles.date} className="fs-mask">
            {customer?.consumerName}
          </h4>
          <p>{productFormatter(customer?.productClassification)}</p>
        </div>
        <div css={styles.orderDetails}>
          <p>{dateFormatter(date, 'day')}</p>
          <Labels.Basic
            variant={getStatusVariant(customer?.status)}
            label={statusFormatter(customer?.status)}
            css={styles.label}
          />
        </div>
      </div>
    </MuiPaper>
  );
};

export const User: FC<UserCardProps> = ({
  user,
  selected = false,
  key,
  actions = [],
  show2StepVerification = false,
  onMfaSwitchToggled,
  ...props
}) => {
  const { isCompanyUser } = useMerchantData();

  return (
    <MuiPaper css={styles.paper(selected, true, false)} key={key} {...props}>
      <div css={styles.row('stretch')}>
        <div css={styles.column}>
          <div css={styles.row('flex-start')}>
            <div css={styles.column(false, true)}>
              <h4 css={styles.date} className="fs-mask">
                {user?.firstName} {user?.lastName}
              </h4>
              <p className="body2" css={styles.userDescription}>
                {user.roleName}
                {isCompanyUser && <> | {user.userType}</>}
              </p>
            </div>

            {actions?.length >= 1 && (
              <div css={styles.actions}>
                {actions?.map((action) => (
                  <>{action}</>
                ))}
              </div>
            )}
          </div>

          {show2StepVerification && (
            <>
              <div css={styles.separator(selected)} />

              <div css={styles.row('center')}>
                <p className="body4">2-step verification</p>
                <div css={styles.switchContent}>
                  {mapMfaStatus(user.mfaEnrollments[0]?.status) ===
                    'Pending' && (
                    <Tooltip
                      id={`mobile-mfa-status-${user.userProfileId}`}
                      content={
                        <>
                          <span className="body3">Setup pending</span>
                          <br />
                          <span className="body4">
                            2-step verification has been enabled for this user
                            but they still need to register their phone number
                            during their next sign-in.
                          </span>
                        </>
                      }
                      placement="bottom"
                      maxWidth={250}
                      arrow
                      interactive
                      dense
                      css={styles.tooltip}
                    >
                      <Icons.InformationCircle />
                    </Tooltip>
                  )}

                  <Widgets.Switch
                    color="primary"
                    label={mapMfaStatus(user.mfaEnrollments[0]?.status)}
                    labelPlacement="start"
                    css={styles.toggle}
                    onChange={(event: any): void =>
                      onMfaSwitchToggled(event, user)
                    }
                    checked={
                      mapMfaStatus(user.mfaEnrollments[0]?.status) !==
                      MfaEnrollmentStatus[MfaEnrollmentStatus.Disabled]
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </MuiPaper>
  );
};

export const ProductConfig: FC<ProductConfigProps> = ({
  key,
  selected,
  productConfig,
  ...props
}) => {
  return (
    <MuiPaper css={styles.paper(selected, true, false)} key={key} {...props}>
      <div css={styles.column} style={{ gap: '8px' }}>
        <div className="body1">
          {ProductName.get(productConfig.Classification)}
        </div>

        <div css={styles.row('center')}>
          <div
            className="body4"
            style={{ color: `${theme.colors.neutral[400]}` }}
          >
            Offer
          </div>
          <div className="body2">
            {getDescriptionForInterestFreeMonths(
              productConfig.Classification,
              productConfig.InterestFreeMonths
            )}
          </div>
        </div>

        <div css={styles.row('center')}>
          <div className="body4" style={{ color: theme.colors.neutral[400] }}>
            Transaction value
          </div>
          <div>{`${currencyFormatter(
            productConfig.TransactionLimitMin
          )} - ${currencyFormatter(productConfig.TransactionLimitMax)}`}</div>
        </div>

        <ProductStatusLabels product={productConfig} />
      </div>
    </MuiPaper>
  );
};
